import { useMemo, useState, type ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useNavTitle } from '../lib/nav'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { capture } from '@region13/analytics'
import {
  emptyListMessage, listView, useRouteListState, useToast,
  type ListStateProps,
} from '@region13/ui'
import { leagueToday, teamLabel, validateListSearch, type GameRow } from '@region13/shared-types'
import { api, collectsGameCards, type CoachGame, type CoachTeamWithGames } from '../lib/api'
import { Card } from '../components/ui/Card'
import { TeamScope } from '../components/TeamScope'
import { GameFilterBar } from '../components/GameFilterBar'
import { gameLine, opponentOf } from '../lib/nextGame'
import { COACH_GAMES_LIST, coachGameFacets } from '../lib/coachFacets'
import { twoUpGamesLabel } from '../lib/gameCards'
import { SCHEDULE_NOT_PUBLISHED_MESSAGE, GAME_CARDS_NOT_USED_MESSAGE, TWO_UP_PRINT_HINT, CUP_CARD_HINT } from '../lib/scheduleCopy'

export const Route = createFileRoute('/cards')({ component: GameCards, validateSearch: validateListSearch })

/** A card download that shows progress. Generating a card is a real-time event
 *  (the server renders the PDF and pulls the roster on first request, which can
 *  take a few seconds), so a plain link feels like a hang. This fetches the PDF
 *  with a spinner + disabled state, opens it when ready, and surfaces a clear
 *  toast if it fails — rather than leaving a blank tab spinning. */
// Waits between download attempts (ms). ~25s total across 5 tries covers a
// container cold boot (~10-30s) without giving up too early.
const CARD_RETRY_BACKOFF_MS = [2000, 4000, 7000, 12000]

/** Pull the filename out of a Content-Disposition header
 *  (`inline; filename="Game Card - B10-03.pdf"`), falling back to a generic
 *  name. */
function filenameFromDisposition(header: string | null): string {
  const m = header ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header) : null
  return m?.[1]?.trim() || 'game-card.pdf'
}

/** Which kind of card `CardDownloadButton` is downloading, and — for a real
 *  game card — what to send if the download succeeds.
 *
 *  ⚠️ A DISCRIMINATED UNION ON PURPOSE, not an optional `division?: string`.
 *  An optional prop that doubles as a control flag (`if (division) capture(...)`)
 *  is inferred, not declared: a future caller could pass `division` to the
 *  blank-card button for an unrelated reason (say, to label it) and silently
 *  start firing `game_card_opened` there too, and a render-only test would
 *  still pass. `kind` makes "is this a game card" the one thing a caller has
 *  to state, not a side effect of what else it happened to pass. */
/** Single card, or a two-up sheet (two cards side by side on one landscape
 *  page). Rides on `CardDownload` rather than being inferred from the URL,
 *  because it is also an analytics property — and stated by every caller for
 *  the same reason `kind` is: so no call site gets a layout by default. */
type CardLayout = 'single' | 'two_up'

type CardDownload =
  | { kind: 'blank'; layout: CardLayout }
  | { kind: 'game'; division: string; layout: CardLayout }

/** The three fetch/display facts a download button needs, grouped: none of
 *  the three means anything alone (an `href` with no `team` to blame a
 *  failure on, an `ariaLabel` for a fetch nobody named), so they travel as one
 *  prop rather than three, keeping this component's own prop count at 3
 *  (`target`, `card`, `children`) instead of 5. `team` labels the failure
 *  toast, and is not decoration: this route renders through `TeamScope`,
 *  which stacks a card per team for a multi-team coach, and an error toast
 *  never auto-dismisses (`packages/ui` Toast: errors are durable so they can
 *  be read and copied) — an unlabeled banner outlives the panel that raised
 *  it and reads as a failure of whichever panel the coach happens to be
 *  looking at, the same misreading a two-team tester reported on the practice
 *  form on 2026-08-24. */
type CardTarget = { href: string; ariaLabel: string; team: string }

function CardDownloadButton({ target, card, children }: {
  target: CardTarget
  card: CardDownload
  children: ReactNode
}) {
  const { href, ariaLabel, team } = target
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  async function download() {
    if (loading) return
    setLoading(true)
    try {
      // The PDF container sleeps when idle, so the first request of a session
      // can fail while it cold-boots. Retry on cold-start-shaped failures (5xx
      // or a dropped connection) with a growing backoff; fail fast on a 4xx
      // (no access / unknown game) since retrying can't fix that. (A dedicated
      // "Game cards" nav item could later pre-warm on click; see TODO.)
      let blob: Blob | null = null
      let filename = 'game-card.pdf'
      for (let attempt = 0; attempt <= CARD_RETRY_BACKOFF_MS.length; attempt++) {
        const r = await fetch(href, { credentials: 'same-origin' }).catch(() => null)
        if (r?.ok) {
          blob = await r.blob()
          // The server sets a friendly name (e.g. "Game Card - B10-03 Sep 9
          // 2026.pdf") in Content-Disposition. A blob: URL would discard it, so
          // read it here and apply it to the download.
          filename = filenameFromDisposition(r.headers.get('content-disposition'))
          break
        }
        if (r && r.status < 500) throw new Error(`card ${r.status}`)
        const wait = CARD_RETRY_BACKOFF_MS[attempt]
        if (wait) await new Promise((res) => setTimeout(res, wait))
      }
      if (!blob) throw new Error('card unavailable')
      // ⚠️ DIVISION ONLY — never `team` (an id) paired with anything from the
      // roster the server just rendered into this PDF, and no season id: it
      // is never surfaced to this client. `division` alone is a label. Gated
      // on `card.kind`, not on whether `division` happens to be present.
      if (card.kind === 'game') capture('game_card_opened', { division: card.division, layout: card.layout })
      // Download with the friendly filename (an <a download> keeps the name; a
      // plain window.open on a blob URL would not).
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      toast.error(`${team}: could not generate that card. Please try again in a moment.`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={download}
      disabled={loading}
      aria-label={ariaLabel}
      className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 text-sm text-brand-red-dark underline disabled:opacity-60"
    >
      {loading && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
      {loading ? 'Generating…' : children}
    </button>
  )
}

/** The team's games, narrowed by the screen's one filter bar (`GameFilterBar`).
 *  Only rendered for a team that has games: a team with none keeps the
 *  schedule-not-published message above. */
function CardGameList({ team, games, listState }: { team: CoachTeamWithGames; games: CoachGame[]; listState: ListStateProps }) {
  const rows = useMemo(() => games.map((g): CoachGame & GameRow => ({ ...g, division: '' })), [games])
  const facets = useMemo(() => coachGameFacets(rows), [rows])
  const { visible } = listView({ rows, facets, state: listState.state, today: leagueToday(Date.now()) })
  if (visible.length === 0) return <p className="text-sm text-ink-soft">{emptyListMessage(listState)}</p>
  return (
    <ul className="divide-y divide-line">
      {visible.map((g) => {
        const opponent = opponentOf(g, team.externalId)
        // Defense in depth (design spec §8: no game cards for Extra
        // games). The team-level `collectsGameCards` gate above already
        // withholds the whole panel from an Extra team, since that team
        // never plays a game (teamPlaysGamesSql) — this is the SEPARATE,
        // per-game guard so a future bug in that gate cannot resurrect a
        // download button on an external row.
        const isExternal = !!g.external_opponent
        return (
          <li key={g.id} className="flex items-center justify-between gap-3 py-1.5 text-sm text-ink">
            {/* min-w-0 lets a long field name wrap instead of shoving the shrink-0 button off-screen */}
            <span className="min-w-0">{gameLine(g, opponent)}</span>
            {isExternal ? null : (
              <CardDownloadButton
                target={{
                  href: api.cardUrl(team.externalId, g.id),
                  ariaLabel: `Download the game card for ${gameLine(g, opponent)}`,
                  team: team.externalId,
                }}
                card={{ kind: 'game', division: team.division, layout: 'single' }}
              >
                Download card
              </CardDownloadButton>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** Every listed card-collecting team's games, for the screen's one filter bar.
 *  Shares the panels' `team-games` query key, so a superadmin's selected team
 *  is fetched once. */
export function CardsFilterBar({ teams, adminSelected, listState }: { teams: CoachTeamWithGames[]; adminSelected: boolean; listState: ListStateProps }) {
  const eligible = teams.filter(collectsGameCards)
  const queries = useQueries({
    queries: eligible.map((t) => ({
      queryKey: ['team-games', t.externalId],
      queryFn: () => api.teamGames(t.externalId),
      enabled: adminSelected,
    })),
  })
  const rows = eligible
    .flatMap((t, i) => (adminSelected ? (queries[i]?.data?.games ?? []) : t.games))
    .map((g): CoachGame & GameRow => ({ ...g, division: '' }))
  return <GameFilterBar rows={rows} listState={listState} />
}

/** The Cup card's button and what it holds, for a team in the Cup. It rides the same download
 *  control as the other cards, so it fetches, retries and names the file the same way. */
function CupCardAction({ team }: { team: CoachTeamWithGames }) {
  if (!team.cup) return null
  const name = team.name || team.externalId
  return (
    <>
      <CardDownloadButton
        target={{ href: api.cupCardUrl(team.externalId), ariaLabel: `Print a Cup card for ${name}`, team: team.externalId }}
        card={{ kind: 'blank', layout: 'single' }}
      >
        Print a Cup card
      </CardDownloadButton>
      <p className="text-sm text-ink-soft">{CUP_CARD_HINT}</p>
    </>
  )
}

/** One team's card panel: blank-card download + its games, each with a per-game
 *  download. Games are inline for a coach; a superadmin-selected team loads them
 *  on demand (`adminSelected`). */
/** Exported for tests, exactly as referees.tsx exports TeamCrew: the panel is
 *  the unit worth asserting, and reaching it through TeamScope would drag the
 *  whole team-fetch and coach-vs-admin frame into every case. */
export function TeamCard({ team, adminSelected, listState }: { team: CoachTeamWithGames; adminSelected: boolean; listState: ListStateProps }) {
  const gamesQ = useQuery({
    queryKey: ['team-games', team.externalId],
    queryFn: () => api.teamGames(team.externalId),
    // Hooks run before the early return below, so this has to carry the card
    // rule too: without it a superadmin selecting an 8U team still fetches a
    // game list that panel will never render.
    enabled: adminSelected && collectsGameCards(team),
  })
  const games = adminSelected ? (gamesQ.data?.games ?? []) : team.games
  // null when nothing is upcoming, which hides the button rather than offering
  // a sheet the server would decline.
  const pairLabel = twoUpGamesLabel(games)
  // What the aria labels call this team. Not `teamLabel` (already imported
  // here, and used for the visible heading): a screen-reader label wants the
  // plain name, not the designation · coach · name composite.
  const ariaTeamName = team.name || team.externalId
  // ⚠️ The whole panel, not just the buttons. A division that does not use cards
  // has nothing to offer per game either, so rendering the game list under a
  // hidden download would invite the question this message answers. The worker
  // refuses these routes with a 404 regardless — this is the affordance, that is
  // the boundary, and neither substitutes for the other.
  if (!collectsGameCards(team)) {
    return (
      <Card as="section">
        <h2 className="font-semibold text-ink">
          {teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })}
        </h2>
        <p className="text-sm text-ink-soft">{team.division}</p>
        <p className="text-sm text-ink-soft">{GAME_CARDS_NOT_USED_MESSAGE}</p>
      </Card>
    )
  }
  return (
    // A per-team panel: keep the <section> semantics, reuse the Card surface.
    <Card as="section">
      <h2 className="font-semibold text-ink">
        {teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })}
      </h2>
      <p className="text-sm text-ink-soft">{team.division}</p>
      <CardDownloadButton
        target={{
          href: api.blankCardUrl(team.externalId),
          ariaLabel: `Download a blank game card for ${ariaTeamName}`,
          team: team.externalId,
        }}
        card={{ kind: 'blank', layout: 'single' }}
      >
        Download blank card
      </CardDownloadButton>
      <CardDownloadButton
        target={{
          href: api.blankPairCardUrl(team.externalId),
          ariaLabel: `Download two blank game cards on one sheet for ${ariaTeamName}`,
          team: team.externalId,
        }}
        card={{ kind: 'blank', layout: 'two_up' }}
      >
        Download two blank cards
      </CardDownloadButton>
      {pairLabel && (
        <CardDownloadButton
          target={{
            href: api.pairCardUrl(team.externalId),
            ariaLabel: `${pairLabel} for ${ariaTeamName}`,
            team: team.externalId,
          }}
          card={{ kind: 'game', division: team.division, layout: 'two_up' }}
        >
          {pairLabel}
        </CardDownloadButton>
      )}
      <p className="text-sm text-ink-soft">{TWO_UP_PRINT_HINT}</p>
      <CupCardAction team={team} />
      {adminSelected && gamesQ.isLoading ? (
        <p role="status" className="text-sm text-ink-soft">Loading games…</p>
      ) : adminSelected && gamesQ.isError ? (
        // Distinct from "no games" — a failed fetch must not read as an empty schedule.
        <p role="alert" className="text-sm text-brand-red-dark">Couldn't load games. Please try again.</p>
      ) : games.length === 0 ? (
        <p className="text-sm text-ink-soft">{SCHEDULE_NOT_PUBLISHED_MESSAGE}</p>
      ) : (
        <CardGameList team={team} games={games} listState={listState} />
      )}
    </Card>
  )
}

/** Game cards: a coach's teams (games inline), or a superadmin's selected team
 *  (games on demand) — the coach-vs-admin frame lives in TeamScope. */
function GameCards() {
  const listState = useRouteListState(COACH_GAMES_LIST)
  return (
    <TeamScope
      heading={useNavTitle()}
      emptyText="No teams found for your account this season."
      hint="Pick a team to view its games and cards."
      bar={(teams, adminSelected) => <CardsFilterBar teams={teams} adminSelected={adminSelected} listState={listState} />}
    >
      {(team, adminSelected) => <TeamCard team={team} adminSelected={adminSelected} listState={listState} />}
    </TeamScope>
  )
}
